import { FC, useEffect, useState } from "react"
import { Flex, Box, Text, Card } from 'rebass/styled-components'
import { BASELINE_QUESTIONS } from "../../surveys/questions/baselineQuestions"
import { ISurvey, ISurveyOption } from "../../surveys/questions/common"
import { QUESTIONS_FOLLOWUP_SURVEYS_1 } from "../../surveys/questions/followUp1Questions"
import { QUESTIONS_FOLLOWUP_SURVEYS_2 } from "../../surveys/questions/followUp2Questions"
import { getDefaultValueFreeText, getDefaultValueMultiple, getDefaultValueMultipleDetails } from "../../surveys/utils"
import moment from "moment"
import { QUESTIONS_FOLLOWUP_SURVEYS_3 } from "../../surveys/questions/followUp3Questions"

const SelectButton = ({ text, isSelected, hasSelection, score }: { text: string, isSelected: boolean, hasSelection: boolean, score: number | undefined }) => {

  const colorsUnselected = [
    '#FFBDB2',
    '#F3C8BD',
    '#E4D7C7',
    '#D8E4D4',
    '#C9F2DF',
    '#BCFFEB',
  ]
  // console.log({ score })
  return (
    <Box
      style={{
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        opacity: !!hasSelection ? (isSelected ? 1 : 0.35) : 1,
        // minWidth: '20%',
        cursor: 'pointer',
        border: `1px solid ${isSelected ? '#344f79' : '#5f769a'}`,
        background: score !== undefined ? `linear-gradient(to right, ${colorsUnselected[score]}, ${colorsUnselected[score + 1]})` : '#e0e3e9',
        padding: '8px 16px',
        fontSize: '18px',
      }}
      mr={['4px', 3]}
      mb={['4px', 3]}
    >
      {text}
    </Box>
  );
};
interface ISingleSurveyAnswers {
  surveyName: string,
  answers: string,
  lastUpdate?: string
}
interface ISurveyAnswers {
  answers: Array<ISingleSurveyAnswers>
}

const parseBaselineAnswers = (answers: string, questions: Array<ISurvey>): Array<{
  title: string;
  id: string;
  selection: (string | undefined)[];
  type: string;
  options: ISurveyOption[] | undefined;
  details?: string;
}> => {

  const parsed: Array<{
    id: string,
    selection: Array<{
      value: string,
      details: string
    }>
  }> = JSON.parse(answers)
  const result = []
  for (let index = 0; index < questions.length; index++) {
    const element = questions[index];
    const ans = parsed.find(x => x.id === element.id)
    switch (element.type) {
      case 'PICKONE':
        const s = getDefaultValueMultiple(ans)
        result.push({
          title: element.title,
          id: element.id,
          selection: s,
          type: 'PICKONE',
          options: element.options
        })
        break;
      case 'PICKONEOTHER':
        const s2 = getDefaultValueMultiple(ans)
        const ot = getDefaultValueMultipleDetails(ans)
        result.push({
          title: element.title,
          id: element.id,
          selection: s2,
          details: ot,
          type: 'PICKONEOTHER',
          options: element.options
        })

        break;
      case 'PICKMULTIPLEOTHER':
        const s3 = getDefaultValueMultiple(ans)
        const ot1 = getDefaultValueMultipleDetails(ans)
        result.push({
          title: element.title,
          id: element.id,
          selection: s3,
          details: ot1,
          type: 'PICKMULTIPLEOTHER',
          options: element.options
        })

        break;
      case 'OPENTEXT':
        const ft = getDefaultValueFreeText(ans)
        result.push({
          title: element.title,
          id: element.id,
          selection: [],
          details: ft,
          type: 'OPENTEXT',
          options: element.options
        })

        break;

      default:
        break;
    }
  }

  return result
}
export const SurveyAnswers: FC<ISurveyAnswers> = ({ answers }) => {
  const [data, setData] = useState<Array<{
    survey: string,
    data: Array<{
      title: string;
      id: string;
      selection: (string | undefined)[];
      type: string;
      details?: string;
      options: ISurveyOption[] | undefined;
    }>
  }>>()

  useEffect(() => {
    getBaseline()
  }, [])

  useEffect(() => {
    getBaseline()
  }, [answers])

  const getBaseline = () => {
    const baseline = answers.find(x => x.surveyName.toLowerCase() === 'baseline')?.answers
    if (!baseline) return
    const d = parseBaselineAnswers(baseline, BASELINE_QUESTIONS)
    const res: Array<{
      survey: string,
      data: Array<{
        title: string;
        id: string;
        selection: (string | undefined)[];
        type: string;
        details?: string;
        options: ISurveyOption[] | undefined;
      }>
    }> = []

    const bl = {
      survey: 'Baseline',
      data: d
    }
    res.push(bl)
    let followup = {}

    const fu1 = answers.find(x => x.surveyName.toLowerCase() === 'followupsurvey1')?.answers
    if (fu1) {
      const d1 = parseBaselineAnswers(fu1, QUESTIONS_FOLLOWUP_SURVEYS_1)
      followup = d1
      res.push({
        survey: 'Follow-up 1',
        data: d1
      })
    }

    const fu2 = answers.find(x => x.surveyName.toLowerCase() === 'followupsurvey2')?.answers
    if (fu2) {
      const d2 = parseBaselineAnswers(fu2, QUESTIONS_FOLLOWUP_SURVEYS_2)
      followup = d2
      res.push({
        survey: 'Follow-up 2',
        data: d2
      })
    }

    const fu3 = answers.find(x => x.surveyName.toLowerCase() === 'followupsurvey3')?.answers
    if (fu3) {
      const d3 = parseBaselineAnswers(fu3, QUESTIONS_FOLLOWUP_SURVEYS_3)
      followup = d3
      res.push({
        survey: 'Follow-up 3',
        data: d3
      })
    }

    setData(res)
  }

  if (!data) return null
  return (
    <Flex flexDirection={'column'}>
      {data?.map(x => {
        return (
          <Card style={{ width: '100%', borderRadius: '30px', padding: '20px', flex: 1 }} mb={4}>
            <Flex>
              <Flex flexDirection={'column'} flex='1'>
                <Text fontWeight={600} fontSize='16px' mb={3}>{x.survey} - answers</Text>
                {x.data.map(y => {
                  return (<Flex padding={'12px 0'} style={{ borderBottom: '1px solid rgba(0,0,0,0.1)', overflow: 'hidden' }} mb={'12px'} flexDirection='column'>
                    <Box mb={3}>
                      {y.title}
                    </Box>
                    <Flex flex={1} flexWrap='wrap' style={{ paddingLeft: '100px' }}>
                      {y.options?.map((z, optI) => {
                        return (
                          <SelectButton text={z.text} hasSelection={y.selection && y.selection.length > 0} isSelected={y.selection.findIndex(aa => aa === z.text) > -1} score={z.score} />
                        )
                      })}
                    </Flex>
                    {!!y.details && (
                      <Flex flexDirection={'column'} style={{ paddingLeft: '100px' }}>
                        <Text>Patient input:</Text>
                        <Box style={{ border: '1px solid rgb(52, 79, 121)', padding: '12px 24px', backgroundColor: 'white' }}>
                          <Text>{y.details}</Text>
                        </Box>
                      </Flex>
                    )}
                    {(!y.options || y.options.length < 1) && !y.details && (
                      <Flex flexDirection={'column'} style={{ paddingLeft: '100px' }}>
                        <Text fontWeight={300} opacity='0.75'>Not answered</Text>
                      </Flex>
                    )}
                  </Flex>)
                })}
              </Flex>
            </Flex>
          </Card>
        )
      })}
    </Flex>
  )
}


export const SingelSurveyAnswers: FC<ISingleSurveyAnswers> = ({ answers, surveyName, lastUpdate }) => {
  const [data, setData] = useState<{
    survey: string,

    data: Array<{
      title: string;
      id: string;
      selection: (string | undefined)[];
      type: string;
      details?: string;
      options: ISurveyOption[] | undefined;
    }>
  }>()

  useEffect(() => {
    getBaseline()
  }, [])

  useEffect(() => {
    getBaseline()
  }, [answers])

  const getBaseline = () => {
    const baseline = answers
    if (!baseline) return
    let sn = ''
    let quests: ISurvey[] = BASELINE_QUESTIONS
    switch (surveyName.toLowerCase()) {
      case 'baseline':
        sn = 'Baseline'
        quests = BASELINE_QUESTIONS
        break;
      case 'followupsurvey1':
        sn = 'followup 1'
        quests = QUESTIONS_FOLLOWUP_SURVEYS_1
        break;
      case 'followupsurvey2':
        sn = 'followup 2'
        quests = QUESTIONS_FOLLOWUP_SURVEYS_2
        break;


      default:
        break;
    }
    // if (surveyName === 'baseline')
    const d = parseBaselineAnswers(baseline, quests)
    const res: Array<{
      survey: string,
      data: Array<{
        title: string;
        id: string;
        selection: (string | undefined)[];
        type: string;
        details?: string;
        options: ISurveyOption[] | undefined;
      }>
    }> = []

    const bl = {
      survey: sn,
      data: d
    }
    // res.push(bl)

    setData(bl)
  }

  if (!data) return null
  return (
    <Flex flexDirection={'column'} width='100%' padding={'20px'}>
      <Flex>
        <Flex flexDirection={'column'} flex='1'>
          <Flex alignItems={'baseline'}>

            <Text fontWeight={600} fontSize='16px' mb={3}>{data.survey} - answers</Text>
            {!!lastUpdate && (
              <Text fontWeight={300} fontSize='14px' mb={3} ml={2}>last updated: {moment(lastUpdate).format('MM/DD/YYYY')}</Text>
            )}
          </Flex>
          {/* <Text fontWeight={600} fontSize='16px' mb={3}>{data.survey} - answers</Text> */}
          {data.data.map(y => {
            return (<Flex padding={'12px 0'} style={{ borderBottom: '1px solid rgba(0,0,0,0.1)', overflow: 'hidden' }} mb={'12px'} flexDirection='column'>
              <Box mb={3}>
                {y.title}
              </Box>
              <Flex flex={1} flexWrap='wrap' style={{ paddingLeft: '100px' }}>
                {y.options?.map((z, optI) => {
                  return (
                    <SelectButton text={z.text} hasSelection={y.selection && y.selection.length > 0} isSelected={y.selection.findIndex(aa => aa === z.text) > -1} score={z.score} />
                  )
                })}
              </Flex>
              {!!y.details && (
                <Flex flexDirection={'column'} style={{ paddingLeft: '100px' }}>
                  <Text>Patient input:</Text>
                  <Box style={{ border: '1px solid rgb(52, 79, 121)', padding: '12px 24px', backgroundColor: 'white' }}>
                    <Text>{y.details}</Text>
                  </Box>
                </Flex>
              )}
              {(!y.options || y.options.length < 1) && !y.details && (
                <Flex flexDirection={'column'} style={{ paddingLeft: '100px' }}>
                  <Text fontWeight={300} opacity='0.75'>Not answered</Text>
                </Flex>
              )}
            </Flex>)
          })}
        </Flex>
      </Flex>
    </Flex>
  )
}
